import {Alert, AlertDescription, AlertTitle, Button} from '@tinct/web-components';
import {CircleAlert, PencilIcon, Sparkles} from 'lucide-react';
import {FormProvider} from 'react-hook-form';
import {createConnectedInputHelpers} from '@tinct/web-form';
import {useFunnel} from '@tinct/web-funnel';
import {useUpdateOrganizationDomainForm} from "../../../hooks/use-edit-organization-website-form";
import {useGetCurrentWorkspaceId} from "@tinct/core-workspace";
import {UpdateOrganization} from "@tinct/core-api-models";
import type {BrandExtractionFunnelState} from "../brand-extraction-funnel";

export const Step1FillWebsiteUrl = () => {
  const workspaceId = useGetCurrentWorkspaceId();
  const { navigateToNextStep, navigateToStep, state } = useFunnel<BrandExtractionFunnelState>();

  const getQueryParams = new URLSearchParams(window.location.search);
  const isError = getQueryParams.get('error') === 'true';

  const { form, handleSubmit, isLoading, isFetchingOrganization } = useUpdateOrganizationDomainForm({
    organizationId: workspaceId,
    onSuccess: () => {
      // Before moving on: Processing must wait for the extraction of this website, not take a
      // finished previous one for it.
      state.requestExtraction();
      navigateToNextStep();
    }
  });

  const { ControlledTextInput } =
    createConnectedInputHelpers<UpdateOrganization>();

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col w-full "
    >
      <FormProvider {...form}>
        {/* Main content */}
        <div className="flex-1 flex items-center justify-center px-4 container-4xl">
          <div className="w-full max-w-md space-y-6">
            {/* Heading */}
            <div className="space-y-1 text-center">
              <h1 className="text-3xl font-bold text-gray-900 font-heading">
                Sync your Brand.
              </h1>
              <h2 className="text-3xl font-bold text-gray-900 font-heading">
                Let our AI do the heavy lifting.
              </h2>
            </div>

            {/* Description */}
            <p className="text-gray-500 text-sm">
              We'll fetch your brand's guidelines to ensure everything we
              generate stays aligned
            </p>

            {/* Form */}
            <div className="space-y-4 pt-2">
              <ControlledTextInput
                name={'domain'}
                label={'Website URL'}
                inputPrefix={'https://'}
                trimEnd={'/'}
              />

              {isError && (
                <Alert className="bg-rose-50 border-rose-200 text-destructive">
                  <CircleAlert className="size-4" />
                  <AlertTitle>Something went wrong!</AlertTitle>
                  <AlertDescription className="text-destructive/90">
                    Unable to access your website.
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type={'submit'}
                isLoading={isLoading || isFetchingOrganization}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Analyze
              </Button>
            </div>
            {isError && (
              <div className="flex flex-row gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => {
                  navigateToStep(2);
                }}>
                  <PencilIcon />Set up manually
                </Button>
                <Button type="button" variant="outline" className="flex-1">
                  Get Help
                </Button>
              </div>
            )}
          </div>
        </div>
      </FormProvider>
    </form>
  );
};
